import React, { useState } from 'react';
import { useJarvis } from '../../hooks/useJarvis';
import { useJarvisContext } from '../../context/JarvisContext';
import { getTodayLesson, getRecentLessons } from '../../services/storage';
import { getTodayDate, getDayOfWeek, getCurrentSession } from '../../utils/dateUtils';
import { THEME } from '../../styles/theme';

// ── Mood options ──────────────────────────────────────────────────────
const MOODS = [
  { label: 'Focused',    emoji: '🎯', value: 'focused' },
  { label: 'Calm',       emoji: '😌', value: 'calm' },
  { label: 'Anxious',    emoji: '😰', value: 'anxious' },
  { label: 'Excited',    emoji: '🔥', value: 'excited' },
  { label: 'Frustrated', emoji: '😤', value: 'frustrated' },
  { label: 'Tired',      emoji: '😴', value: 'tired' },
];

const SESSIONS = ['Pre-London', 'London', 'London+NY', 'NY Close', 'Asian', 'Multiple'];

const S = {
  page: {
    padding:       '20px',
    maxWidth:      '100%',
    margin:        '0 auto',
    paddingBottom: '40px',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    fontSize:     '22px',
    fontWeight:   '700',
    color:        THEME.colors.gold,
    letterSpacing: '2px',
    fontFamily:   THEME.fonts.hud,
  },
  subtitle: {
    fontSize:  '13px',
    color:     THEME.colors.textMuted,
    marginTop: '4px',
  },
  section: {
    background:   'rgba(255,255,255,0.03)',
    border:       '1px solid rgba(255,255,255,0.07)',
    borderRadius: '12px',
    padding:      '18px',
    marginBottom: '14px',
  },
  sectionTitle: {
    fontSize:     '11px',
    fontWeight:   '700',
    color:        THEME.colors.textMuted,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    marginBottom: '12px',
    fontFamily:   THEME.fonts.hud,
  },
  textarea: {
    width:        '100%',
    background:   'rgba(255,255,255,0.05)',
    border:       '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    color:        THEME.colors.text,
    fontSize:     '14px',
    padding:      '10px 12px',
    resize:       'vertical',
    minHeight:    '72px',
    outline:      'none',
    fontFamily:   THEME.fonts.body,
    lineHeight:   '1.6',
    boxSizing:    'border-box',
  },
  row: {
    display:   'flex',
    gap:       '10px',
    flexWrap:  'wrap',
    alignItems: 'center',
  },
  moodBtn: (selected) => ({
    padding:      '7px 14px',
    borderRadius: '20px',
    border:       selected ? `1px solid ${THEME.colors.gold}` : '1px solid rgba(255,255,255,0.1)',
    background:   selected ? 'rgba(255,215,0,0.1)' : 'transparent',
    color:        selected ? THEME.colors.gold : THEME.colors.textMuted,
    fontSize:     '13px',
    cursor:       'pointer',
    transition:   'all 0.15s',
    display:      'flex',
    alignItems:   'center',
    gap:          '6px',
  }),
  sessionBtn: (selected) => ({
    padding:      '6px 14px',
    borderRadius: '20px',
    border:       selected ? `1px solid ${THEME.colors.blue}` : '1px solid rgba(255,255,255,0.1)',
    background:   selected ? 'rgba(0,212,255,0.1)' : 'transparent',
    color:        selected ? THEME.colors.blue : THEME.colors.textMuted,
    fontSize:     '12px',
    cursor:       'pointer',
    transition:   'all 0.15s',
  }),
  ratingRow: {
    display:    'flex',
    gap:        '8px',
    alignItems: 'center',
  },
  ratingBtn: (selected, val) => {
    const color = val >= 8 ? THEME.colors.green : val >= 5 ? THEME.colors.gold : THEME.colors.red;
    return {
      width:        '36px',
      height:       '36px',
      borderRadius: '8px',
      border:       selected ? `2px solid ${color}` : '1px solid rgba(255,255,255,0.1)',
      background:   selected ? `${color}18` : 'transparent',
      color:        selected ? color : THEME.colors.textMuted,
      fontSize:     '14px',
      fontWeight:   '700',
      cursor:       'pointer',
      transition:   'all 0.15s',
    };
  },
  tradeRow: {
    display:    'flex',
    gap:        '10px',
    alignItems: 'center',
  },
  numInput: {
    width:        '64px',
    background:   'rgba(255,255,255,0.05)',
    border:       '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    color:        THEME.colors.text,
    fontSize:     '16px',
    fontWeight:   '700',
    padding:      '8px',
    textAlign:    'center',
    outline:      'none',
    fontFamily:   THEME.fonts.hud,
  },
  label: {
    fontSize: '13px',
    color:    THEME.colors.textMuted,
  },
  submitBtn: (disabled) => ({
    width:        '100%',
    padding:      '14px',
    borderRadius: '10px',
    border:       'none',
    background:   disabled ? 'rgba(255,255,255,0.05)' : `linear-gradient(135deg, ${THEME.colors.gold}, ${THEME.colors.orange})`,
    color:        disabled ? 'rgba(255,255,255,0.2)' : '#000',
    fontSize:     '15px',
    fontWeight:   '800',
    letterSpacing: '2px',
    cursor:       disabled ? 'not-allowed' : 'pointer',
    fontFamily:   THEME.fonts.hud,
    marginTop:    '8px',
    transition:   'all 0.2s',
  }),
  resultCard: (border) => ({
    background:   'rgba(0,255,136,0.05)',
    border:       `1px solid ${border || 'rgba(0,255,136,0.2)'}`,
    borderRadius: '12px',
    padding:      '20px',
    marginBottom: '16px',
  }),
  resultTitle: {
    fontSize:     '14px',
    fontWeight:   '700',
    color:        THEME.colors.green,
    marginBottom: '10px',
    fontFamily:   THEME.fonts.hud,
    letterSpacing: '1px',
  },
  resultText: {
    fontSize:   '14px',
    color:      THEME.colors.text,
    lineHeight: '1.7',
  },
  pill: (color) => ({
    display:      'inline-block',
    padding:      '3px 10px',
    borderRadius: '20px',
    fontSize:     '12px',
    fontWeight:   '700',
    background:   `${color}18`,
    color:        color,
    border:       `1px solid ${color}33`,
    margin:       '3px',
  }),
  historyCard: {
    background:   'rgba(255,255,255,0.02)',
    border:       '1px solid rgba(255,255,255,0.06)',
    borderRadius: '10px',
    padding:      '14px',
    marginBottom: '10px',
  },
  historyDate: {
    fontSize:     '11px',
    color:        THEME.colors.textMuted,
    fontFamily:   THEME.fonts.hud,
    letterSpacing: '1px',
    marginBottom: '6px',
  },
  historyText: {
    fontSize:   '13px',
    color:      THEME.colors.text,
    lineHeight: '1.6',
  },
};

const emptyForm = {
  market_behavior: '',
  session_traded:  '',
  trade_count:     0,
  wins:            0,
  losses:          0,
  psychology_rating: 0,
  mood:            '',
  what_worked:     '',
  what_failed:     '',
  mistakes:        '',
  tomorrow_plan:   '',
  notes:           '',
};

export default function DailyTeaching() {
  const [form, setForm]       = useState(emptyForm);
  const [result, setResult]   = useState(null);
  const [tab, setTab]         = useState('recap');   // 'recap' | 'history'
  const { isThinking, submitDailyRecap } = useJarvis();
  const { refreshData } = useJarvisContext();

  const todayLesson  = getTodayLesson();
  const recentLessons = getRecentLessons(7);
  const today        = getTodayDate();
  const dayOfWeek    = getDayOfWeek();
  const session      = getCurrentSession();

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.market_behavior.trim()) return;
    try {
      const analysis = await submitDailyRecap({ ...form, date: today, day_of_week: dayOfWeek });
      setResult(analysis);
      refreshData();
    } catch (err) {
      console.error('Recap error:', err);
    }
  };

  // Already submitted today
  if (todayLesson && !result) {
    return (
      <div style={S.page}>
        <div style={S.header}>
          <div style={S.title}>📚 DAILY TEACHING</div>
          <div style={S.subtitle}>{today} · {dayOfWeek}</div>
        </div>
        <div style={S.resultCard('rgba(0,212,255,0.2)')}>
          <div style={{ ...S.resultTitle, color: THEME.colors.blue }}>✅ TODAY'S RECAP SUBMITTED</div>
          <div style={S.resultText}>
            {todayLesson.jarvis_summary || todayLesson.market_behavior}
          </div>
          {todayLesson.jarvis_takeaway && (
            <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(0,212,255,0.05)', borderRadius: '8px', borderLeft: `3px solid ${THEME.colors.blue}` }}>
              <div style={{ fontSize: '11px', color: THEME.colors.textMuted, letterSpacing: '1px', marginBottom: '4px' }}>KEY TAKEAWAY</div>
              <div style={{ fontSize: '14px', color: THEME.colors.text }}>{todayLesson.jarvis_takeaway}</div>
            </div>
          )}
          {todayLesson.tomorrow_watch && (
            <div style={{ marginTop: '10px', fontSize: '13px', color: THEME.colors.gold }}>
              👀 Tomorrow: {todayLesson.tomorrow_watch}
            </div>
          )}
        </div>
        <button style={{ ...S.submitBtn(false), background: 'rgba(0,212,255,0.1)', color: THEME.colors.blue, border: `1px solid ${THEME.colors.blue}44` }}
          onClick={() => { setResult(null); setForm(emptyForm); }}>
          + ADD ANOTHER RECAP
        </button>
      </div>
    );
  }

  // Show result after submission
  if (result) {
    return (
      <div style={S.page}>
        <div style={S.header}>
          <div style={S.title}>📚 JARVIS LEARNED TODAY</div>
          <div style={S.subtitle}>{today} · {dayOfWeek}</div>
        </div>

        {result.summary && (
          <div style={S.resultCard()}>
            <div style={S.resultTitle}>🧠 JARVIS ANALYSIS</div>
            <div style={S.resultText}>{result.summary}</div>
            {result.key_takeaway && (
              <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(0,255,136,0.05)', borderRadius: '8px', borderLeft: `3px solid ${THEME.colors.green}` }}>
                <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '4px' }}>KEY TAKEAWAY</div>
                <div style={{ fontSize: '14px', color: THEME.colors.text }}>{result.key_takeaway}</div>
              </div>
            )}
          </div>
        )}

        {/* What JARVIS saved */}
        <div style={S.section}>
          <div style={S.sectionTitle}>SAVED TO JARVIS KNOWLEDGE</div>
          <div>
            {(result.behaviors_new || []).map((b, i) => (
              <span key={i} style={S.pill(THEME.colors.gold)}>+ Behavior: {b.title}</span>
            ))}
            {(result.patterns_new || []).map((p, i) => (
              <span key={i} style={S.pill(THEME.colors.green)}>+ Pattern: {p.title}</span>
            ))}
            {(result.rules_new || []).map((r, i) => (
              <span key={i} style={S.pill(THEME.colors.blue)}>+ Rule: {r.title}</span>
            ))}
            {!(result.behaviors_new?.length || result.patterns_new?.length || result.rules_new?.length) && (
              <span style={{ fontSize: '13px', color: THEME.colors.textMuted }}>No new items extracted — JARVIS noted this in context.</span>
            )}
          </div>
        </div>

        {result.psychology_feedback && (
          <div style={{ ...S.section, borderColor: 'rgba(255,215,0,0.15)' }}>
            <div style={S.sectionTitle}>PSYCHOLOGY FEEDBACK</div>
            <div style={S.resultText}>{result.psychology_feedback}</div>
          </div>
        )}

        {result.tomorrow_watch && (
          <div style={{ ...S.section, borderColor: 'rgba(0,212,255,0.15)' }}>
            <div style={{ ...S.sectionTitle, color: THEME.colors.blue }}>TOMORROW — WATCH FOR</div>
            <div style={S.resultText}>{result.tomorrow_watch}</div>
          </div>
        )}

        <button style={S.submitBtn(false)} onClick={() => { setResult(null); setForm(emptyForm); }}>
          + NEW RECAP
        </button>
      </div>
    );
  }

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.title}>📚 DAILY TEACHING</div>
        <div style={S.subtitle}>
          {today} · {dayOfWeek}
          {session && ` · ${session.emoji} ${session.name}`}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {['recap', 'history'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding:      '7px 18px',
            borderRadius: '20px',
            border:       tab === t ? `1px solid ${THEME.colors.gold}` : '1px solid rgba(255,255,255,0.1)',
            background:   tab === t ? 'rgba(255,215,0,0.1)' : 'transparent',
            color:        tab === t ? THEME.colors.gold : THEME.colors.textMuted,
            fontSize:     '13px',
            fontWeight:   '700',
            cursor:       'pointer',
            letterSpacing: '1px',
          }}>
            {t === 'recap' ? '📝 Today\'s Recap' : '📅 History'}
          </button>
        ))}
      </div>

      {tab === 'history' ? (
        <div>
          <div style={{ fontSize: '13px', color: THEME.colors.textMuted, marginBottom: '14px' }}>
            Last {recentLessons.length} teaching sessions
          </div>
          {recentLessons.length === 0 && (
            <div style={{ fontSize: '14px', color: THEME.colors.textMuted, textAlign: 'center', padding: '40px' }}>
              No recaps yet. Submit your first daily recap to start teaching JARVIS.
            </div>
          )}
          {recentLessons.map((lesson, i) => (
            <div key={i} style={S.historyCard}>
              <div style={S.historyDate}>{lesson.date} · {lesson.day_of_week} · Mood: {lesson.mood || '—'} · Psychology: {lesson.psychology_rating || '—'}/10</div>
              <div style={S.historyText}>{lesson.jarvis_summary || lesson.market_behavior || 'No summary'}</div>
              {lesson.jarvis_takeaway && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: THEME.colors.blue, fontStyle: 'italic' }}>
                  💡 {lesson.jarvis_takeaway}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Market Behavior */}
          <div style={S.section}>
            <div style={S.sectionTitle}>HOW WAS THE MARKET TODAY? *</div>
            <textarea
              style={S.textarea}
              placeholder="e.g. XAU/USD was bullish. London swept SSL at 2638 then ran up to 2655. NY continuation. Manipulation at open then strong distribution..."
              value={form.market_behavior}
              onChange={e => set('market_behavior', e.target.value)}
            />
          </div>

          {/* Session + Trades */}
          <div style={S.section}>
            <div style={S.sectionTitle}>SESSION TRADED</div>
            <div style={S.row}>
              {SESSIONS.map(s => (
                <button key={s} style={S.sessionBtn(form.session_traded === s)} onClick={() => set('session_traded', s)}>
                  {s}
                </button>
              ))}
            </div>
            <div style={{ ...S.tradeRow, marginTop: '14px' }}>
              <span style={S.label}>Trades taken:</span>
              <input type="number" min="0" max="20" style={S.numInput} value={form.trade_count}
                onChange={e => set('trade_count', parseInt(e.target.value) || 0)} />
              <span style={{ ...S.label, color: THEME.colors.green }}>W:</span>
              <input type="number" min="0" max="20" style={{ ...S.numInput, color: THEME.colors.green }} value={form.wins}
                onChange={e => set('wins', parseInt(e.target.value) || 0)} />
              <span style={{ ...S.label, color: THEME.colors.red }}>L:</span>
              <input type="number" min="0" max="20" style={{ ...S.numInput, color: THEME.colors.red }} value={form.losses}
                onChange={e => set('losses', parseInt(e.target.value) || 0)} />
            </div>
          </div>

          {/* Psychology */}
          <div style={S.section}>
            <div style={S.sectionTitle}>PSYCHOLOGY TODAY</div>
            <div style={S.ratingRow}>
              <span style={S.label}>Score:</span>
              {[1,2,3,4,5,6,7,8,9,10].map(n => (
                <button key={n} style={S.ratingBtn(form.psychology_rating === n, n)}
                  onClick={() => set('psychology_rating', n)}>
                  {n}
                </button>
              ))}
            </div>
            <div style={{ ...S.row, marginTop: '12px' }}>
              {MOODS.map(m => (
                <button key={m.value} style={S.moodBtn(form.mood === m.value)}
                  onClick={() => set('mood', m.value)}>
                  {m.emoji} {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* What worked / failed */}
          <div style={S.section}>
            <div style={S.sectionTitle}>WHAT WORKED TODAY?</div>
            <textarea style={S.textarea} placeholder="e.g. Waited for OB retest, got confirmation, entered with tight SL..."
              value={form.what_worked} onChange={e => set('what_worked', e.target.value)} />
          </div>

          <div style={S.section}>
            <div style={S.sectionTitle}>WHAT FAILED OR WENT WRONG?</div>
            <textarea style={S.textarea} placeholder="e.g. Entered too early before liquidity sweep, chased price in NY..."
              value={form.what_failed} onChange={e => set('what_failed', e.target.value)} />
          </div>

          <div style={S.section}>
            <div style={S.sectionTitle}>MISTAKES / RULE VIOLATIONS</div>
            <textarea style={S.textarea} placeholder="e.g. Broke rule R003 — entered before BOS confirmation. Revenge traded after loss..."
              value={form.mistakes} onChange={e => set('mistakes', e.target.value)} />
          </div>

          <div style={S.section}>
            <div style={S.sectionTitle}>TOMORROW'S PLAN</div>
            <textarea style={S.textarea} placeholder="e.g. Watch 2642 OB for London entry. Bias is bullish until 2660 BSL swept. Avoid trading if psychology < 6..."
              value={form.tomorrow_plan} onChange={e => set('tomorrow_plan', e.target.value)} />
          </div>

          <div style={S.section}>
            <div style={S.sectionTitle}>ANY OTHER NOTES</div>
            <textarea style={{ ...S.textarea, minHeight: '56px' }} placeholder="Anything else JARVIS should know about today..."
              value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>

          <button
            style={S.submitBtn(!form.market_behavior.trim() || isThinking)}
            onClick={handleSubmit}
            disabled={!form.market_behavior.trim() || isThinking}
          >
            {isThinking ? '⏳ JARVIS IS LEARNING...' : '🧠 TEACH JARVIS'}
          </button>
        </>
      )}
    </div>
  );
}

